import { ImageName } from "@mcleod/images";
import {
    ChangeEvent, ClickEvent, DataDisplayEvent, DataSourceMode, DropdownItem, Label, Snackbar, Table, TableRow,
    TableRowMode, TableRowBeforeSaveEvent, LayoutProps, Layout, BlurEvent
} from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutPbwLoadboardServicesSlideout } from "./autogen/AutogenLayoutPbwLoadboardServicesSlideout";
import { CommonDialogs } from "@mcleod/common";
import { UpdateCredentialsDialog } from "./UpdateCredentialsDialog";

const API_SERVICE_TYPES = ['C', 'R'];
const OPERATIONS_USER = new DropdownItem("O", "Operations User");

interface ControlLayoutProps extends LayoutProps {
    vendorId: string;
}

export class PbwLoadboardServicesSlideout extends AutogenLayoutPbwLoadboardServicesSlideout {

    private _vendorId: string;
    private promptTriggered: boolean;
    private updateRateIndexAuthentication: boolean;
    private originalAccountId: string;
    private originalPassword: string;
    private hasRateIndexControl: boolean = false;

    constructor(props?: Partial<ControlLayoutProps>) {
        super(props);
        this._vendorId = props?.vendorId || "";
    }

    private pnnUserData: any[] = [];
    private associatedUserTable: Table;

    buttonImportOnClick(event: ClickEvent) {
        const isTruckstop: boolean = StringUtil.equalsIgnoreCase(this.associatedUserTable.id, "tablePbwUserTruckstop");
        const message = isTruckstop ? "This action will import all existing Truckstop users with a PowerBroker //web user record into the system. \n\nWould you like to proceed?" : "This action will import all existing DAT users with a PowerBroker //web user record into the system. \n\nWould you like to proceed?"
        CommonDialogs.showYesNo(message,
            "Import Users",
            { yesButtonCaption: "Yes", noButtonCaption: "No" }).then(clickedYes => {
                if (clickedYes) {
                    this.pnnUserData?.forEach(async pnnUser => {
                        const existingTableRow = this.associatedUserTable.allRows.find(row => (row.data as ModelRow).get("user_id") === pnnUser?.user_id);
                        if (StringUtil.equalsIgnoreCase(this.associatedUserTable.id, "tablePbwUserTruckstop")) {
                            await this.addAssociatedUserToTruckstopTable(this.associatedUserTable, pnnUser, existingTableRow);
                        } else {
                            await this.addAssociatedUserToConnexionTable(this.associatedUserTable, pnnUser, existingTableRow);
                        }
                    });
                    this.buttonImportUsers.visible = false;
                }

            });
    }


    override async onLoad() {
        this.mainDataSource.addAfterModeChangeListener((event) => {
            if (event.newMode === DataSourceMode.ADD && this.mainDataSource.activeRow) {
                this.activeRow.setValues(this.getDefaultData());
            }
        });
        if (StringUtil.isEmptyString(this._vendorId)) {
            this.textboxServiceType.enabled = true;
        }

        this.addDataDisplayListener((event: DataDisplayEvent) => {
            if (this.mainDataSource?.activeRow?.get("id", null)
                && this.mainDataSource?.activeRow?.get("service_type", null)) {
                const serviceType = this.mainDataSource?.activeRow?.get("service_type", null);
                this.configureFieldsByServiceType(serviceType);
            }

            if (this.mainDataSource.activeRow.get("truckstop_tokens_exist")) {
                this.labelResult.imageName = ImageName.circleCheck;
                this.labelResult.imageColor = "success";
            } else {
                this.labelResult.imageName = ImageName.circleX;
                this.labelResult.imageColor = "error";
                this.labelResult.tooltip = "Tokens do not exist for this service. Please authorize the service."
            }
        })

        this.layoutLoadboardUsersDat.tablePbwUserDat.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => {
            Api.post("lme/general/pbw-loadboard-users", {
                loadboard_id: this.mainDataSource?.activeRow?.get("id", null),
                users: [event.getTableRow().data],
                service_type: "C"
            }).then(response => response?.data[0])
                .then((data) => {
                    const targetRow = this.layoutLoadboardUsersDat.tablePbwUserDat.allRows.find(row => (row.data as ModelRow).get("user_id") === data["user_id"]);
                    if (targetRow != null) {
                        const datError = data["dat_error"];
                        const labelDatStatus = targetRow.findComponentById("labelDatStatus") as Label;

                        if (StringUtil.isEmptyString(datError)) {
                            labelDatStatus.imageName = ImageName.circleCheck;
                            labelDatStatus.imageColor = "success";
                        } else {
                            labelDatStatus.imageName = ImageName.circleX;
                            labelDatStatus.imageColor = "error";
                            labelDatStatus.tooltip = datError;
                            (targetRow.data as ModelRow).set("dat_error", datError);
                        }
                    }
                })
        });

        this.layoutLoadboardUsersTruckstop.tablePbwUserTruckstop.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => {
            Api.post("lme/general/pbw-loadboard-users", {
                loadboard_id: this.mainDataSource?.activeRow?.get("id", null),
                users: [event.getTableRow().data],
                service_type: "R"
            }).then(response => response?.data[0])
                .then((data) => {
                    const targetRow = this.layoutLoadboardUsersTruckstop.tablePbwUserTruckstop.allRows
                        .find(row => (row.data as ModelRow).get("user_id") === data["user_id"]);
                    const labelTruckstopStatus = targetRow.findComponentById("labelTruckstopStatus") as Label;

                    if (!StringUtil.isEmptyString(data.truckstop_access_token)
                        && !StringUtil.isEmptyString(data.truckstop_refresh_token)) {
                        labelTruckstopStatus.imageName = ImageName.circleCheck;
                        labelTruckstopStatus.imageColor = "success";
                    } else {
                        labelTruckstopStatus.imageName = ImageName.circleX;
                        labelTruckstopStatus.imageColor = "error";
                        labelTruckstopStatus.tooltip = "Tokens not found. Please authorize for new credentials.";
                    }
                })
        })
    }


    switchShowHideSftpOnClick() {
        this.panelSftpOptions.visible = this.switchShowHideSftp.checked;
        this.labelLoadboardExclusions.visible = this.switchShowHideSftp.checked;

    }

    textboxServiceTypeOnChange(event: ChangeEvent) {
        const selectedItem = (this.textboxServiceType.items as DropdownItem[]).find((item: DropdownItem) => item.displayValue === event.newValue);
        const serviceType: string | undefined = selectedItem?.value;
        if (serviceType && !StringUtil.isEmptyString(serviceType)) {
            if (!StringUtil.equalsIgnoreCase(serviceType, "T")) {
                this.buttonOrgAuthorize.visible = false;
                this.labelOrgAuthorizationStatus.visible = false;
                this.layoutLoadboardUsersDatRest.visible = false;
            }
            this.configureFieldsByServiceType(serviceType);
        }
    }

    configureFieldsByServiceType(serviceType: string) {
        if (StringUtil.equalsIgnoreCase(serviceType, "T")) {
            this.configureFieldsForDatRest(serviceType);
        } else {
            this.configureDefaultFields();
            this.configureFieldsForApi(serviceType);
            this.configureFieldsForDatConnexion(serviceType);
            this.configureFieldsForTruckstopRest(serviceType);
        }
    }

    private configureDefaultFields() {
        this.textboxUrl.clear();
        this.textboxAccountId.clear();
        this.textboxPassword.clear();
        this.textboxClientId.clear();
        this.textboxClientSecret.clear();

        this.panelPostTiming.visible = true;
        this.switchShowHideSftp.visible = true;
        this.panelOptions.visible = true;
    }

    configureFieldsForApi(serviceType: string) {
        const isApiServiceType = API_SERVICE_TYPES.includes(serviceType);
        this.labelAssociatedUsers.visible = isApiServiceType;
        this.textboxRemoteDirectory.visible = !isApiServiceType;
        this.textboxRemotePort.visible = !isApiServiceType;
        this.textboxSftpFilePrefix.visible = !isApiServiceType;
    }

    async configureFieldsForDatConnexion(serviceType: string) {
        const isDatConnexionServiceType: boolean = serviceType == 'C';
        this.panelDatUserTable.visible = isDatConnexionServiceType;
        if (isDatConnexionServiceType) {
            this.layoutLoadboardUsersDat.mainDataSource.mode = DataSourceMode.UPDATE;
            await this.layoutLoadboardUsersDat.mainDataSource.search({
                loadboard_id: this.mainDataSource?.activeRow?.get("id", null),
                service_type: serviceType
            });
            this.getTranscoreUsers(this.layoutLoadboardUsersDat.tablePbwUserDat);
            this.setConnexionDefaultValues();
        }
    }

    private async configureFieldsForDatRest(serviceType: string) {
        this.configureDatRestFieldVisibility();
        await this.setDatRestDefaultValues(serviceType);
        await this.checkOrganizationAuthorization(serviceType);
    }

    private async setDatRestDefaultValues(serviceType: string) {
        const response = await this.sourceRateIndexControl.search({ "vendor": serviceType });
        const rateIndexControlData = response?.modelRows?.find(row => row.data?.vendor === 'T')?.data;

        if (!rateIndexControlData) {
            return;
        }
        // Set credentials from rate index control if available
        if (!StringUtil.isEmptyString(rateIndexControlData.dat_account_id) ||
            !StringUtil.isEmptyString(rateIndexControlData.dat_password)) {

            this.activeRow.set("user_id", rateIndexControlData.dat_account_id);
            this.activeRow.set("password", rateIndexControlData.dat_password);
            this.hasRateIndexControl = true;
        }

        if (this.activeRow?.get("dat_rest_default_values")) {
            this.activeRow.setValues(this.activeRow.get("dat_rest_default_values"));
        }

        // Uncomment if needed:
        // this.buttonOrgAuthorize.visible = true;
    }

    private async setTruckstopDefaultValues() {
        if (!this.activeRow?.get("truckstop_default_values")) {
            return;
        }
        this.activeRow.setValues(this.activeRow.get("truckstop_default_values"));
    }

    private async setConnexionDefaultValues() {
        if (!this.activeRow?.get("dat_conn_default_values")) {
            return;
        }
        this.activeRow.setValues(this.activeRow.get("dat_conn_default_values"));
    }

    private async checkOrganizationAuthorization(serviceType: string) {
        const response = await this.sourceOrganizationToken.search({ "vendor_id": serviceType });
        const responseData = response?.modelRows[0]?.data;

        if (responseData?.authorization_attempted || responseData?.organization_token) {
            this.setUpAuthLabel(responseData);
        }
    }

    private configureDatRestFieldVisibility() {
        this.layoutLoadboardUsersDatRest.visible = true;
        this.panelDatContactMethods.visible = true;
        this.hideOptionalPanels();
        this.configureRequiredFields();
        this.enableInputFields();
    }

    private hideOptionalPanels() {
        const elementsToHide = [
            this.textboxRemoteDirectory,
            this.textboxRemotePort,
            this.textboxSftpFilePrefix,
            this.panelTruckstop,
            this.panelTruckstopUserTable,
            this.labelAssociatedUsers,
            this.panelDatUserTable
        ];

        elementsToHide.forEach(element => element.visible = false);
    }

    private configureRequiredFields() {
        this.textboxDefaultPostingUserRole.required = false;
        this.textboxAccountId.required = true;
        this.textboxPassword.required = true;
    }

    private enableInputFields() {
        this.textboxAccountId.enabled = true;
        this.textboxPassword.enabled = true;
    }

    textboxDatFieldsOnBlur(event: BlurEvent) {
        if(!StringUtil.isEmptyString(this.mainDataSource.activeRow?.data?.service_type) && StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow?.data?.service_type,"T")) {
            if(event.changedWhileFocused && this.hasValidFieldsForDatRest()){
                // prompt the user to update credentials
                if(this.hasRateIndexControl && !this.promptTriggered) {
                    this.promptUserToUpdateCredentials();
                }else{
                    this.buttonOrgAuthorize.visible = true;
                    this.labelOrgAuthorizationStatus.visible = false;
                }
            }
        }
    }

    textboxDatAuthFieldsOnFocus(event: FocusEvent) {
        if(!StringUtil.isEmptyString(this.mainDataSource.activeRow?.data?.service_type) && StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow?.data?.service_type,"T")) {
            // store the original data for the user_id and password fields
            this.originalAccountId = this.mainDataSource.activeRow?.originalData?.user_id;
            this.originalPassword = this.mainDataSource.activeRow?.originalData?.password;
        }
    }


    hasValidFieldsForDatRest() {
        return !StringUtil.isEmptyString(this.textboxAccountId.text) && !StringUtil.isEmptyString(this.textboxPassword.text) && StringUtil.equalsIgnoreCase(this.textboxServiceType?.selectedItem?.value, "T");
    }


    private setupButtonOrganizationAuthTooltip() {
        if (!this.buttonOrgAuthorize.enabled) {
            this.buttonOrgAuthorize.tooltip = "Please provide all required fields";
        } else {
            this.buttonOrgAuthorize.tooltip = null;
        }
    }


    async buttonOrgAuthorizeOnClick(event: ClickEvent) {
        this.buttonOrgAuthorize.busy = true;
        this.labelOrgAuthorizationStatus.visible = true;
        this.labelOrgAuthorizationStatus.busy = true;

        try {
            const response = await Api.post("lme/general/powerbroker/authorize", {
                "account_id": this.mainDataSource.activeRow.get("user_id"),
                "password": this.mainDataSource.activeRow.get("password"),
                "is_loadboard": true
            });

            const responseData = response?.data[0];
            this.setUpAuthLabel(responseData);
        } catch (error) {
            this.labelOrgAuthorizationStatus.imageName = ImageName.circleX;
            this.labelOrgAuthorizationStatus.imageColor = "error";
            this.labelOrgAuthorizationStatus.tooltip = "An error occurred during service account authorization";
            Snackbar.showWarningSnackbar("An error occurred during service account authorization");
        } finally {
            this.buttonOrgAuthorize.busy = false;
            this.labelOrgAuthorizationStatus.busy = false;
        }
    }

    getTranscoreUsers(associatedUserTable: Table) {
        Api.search("lme/powerbroker/pnn-transcore-users").then(async (result) => {
            if (result?.data?.length > 0) {
                for (const user of result?.data) {
                    const userId = user.user_id;
                    const existingTableRow = associatedUserTable.allRows.find(row => (row.data as ModelRow).get("user_id") === userId);
                    if (existingTableRow == null) {
                        this.pnnUserData.push(user);
                    }
                }
                if (this.pnnUserData.length > 0) {
                    this.buttonImportUsers.visible = true;
                    this.associatedUserTable = associatedUserTable;
                }
            } else {
                this.buttonImportUsers.visible = false;
            }
        });
    }

    getTruckstopPnnTranscoreUsers(associatedUserTable: Table) {
        Api.search("lme/powerbroker/truckstop-pnn-transcore-users").then(async (result) => {
            if (result?.data?.length > 0) {
                for (const user of result?.data) {
                    const userId = user.user_id;
                    const existingTableRow = associatedUserTable.allRows.find(row => (row.data as ModelRow).get("user_id") === userId);
                    if (existingTableRow == null) {
                        this.pnnUserData.push(user);
                    }
                }
                if (this.pnnUserData.length > 0) {
                    this.buttonImportUsers.visible = true;
                    this.associatedUserTable = associatedUserTable;
                }
            } else {
                this.buttonImportUsers.visible = false;
            }
        });
    }


    async configureFieldsForTruckstopRest(serviceType: string) {
        const isTruckstopRestServiceType: boolean = serviceType == 'R';
        this.textboxAccountId.enabled = !isTruckstopRestServiceType;
        this.textboxPassword.enabled = !isTruckstopRestServiceType;
        this.panelTruckstop.visible = isTruckstopRestServiceType;
        this.panelTruckstopUserTable.visible = isTruckstopRestServiceType;
        if (isTruckstopRestServiceType) {
            this.textboxAccountId.clear();
            this.textboxPassword.clear();
            this.textboxVendorUserId.caption = "Truckstop User ID";
            this.layoutLoadboardUsersTruckstop.mainDataSource.mode = DataSourceMode.UPDATE;
            await this.layoutLoadboardUsersTruckstop.mainDataSource.search({
                loadboard_id: this.mainDataSource?.activeRow?.get("id", null),
                service_type: serviceType
            })
            this.getTruckstopPnnTranscoreUsers(this.layoutLoadboardUsersTruckstop.tablePbwUserTruckstop);
            this.setTruckstopDefaultValues();
        } else {
            this.textboxClientId.clear();
            this.textboxClientSecret.clear();
        }
    }

    buttonAuthorizeOnClick(event: ClickEvent) {
        this.labelResult.imageColor = "primary";
        this.labelResult.busy = true;
        const [clientId, clientSecret, tUser, tPass, tUrl] = [
            this.textboxClientId.text,
            this.textboxClientSecret.text,
            this.textboxVendorUserId.text,
            this.textboxTranscorePassword.text,
            this.textboxUrl.text
        ];
        this.validateTruckstop(clientId, clientSecret, tUser, tPass, tUrl, this.labelResult,
            (response) => {
                this.labelResult.busy = false;
                if (response.data[0].success) {
                    this.mainDataSource.activeRow.set("access_token", response.data[0].access);
                    this.mainDataSource.activeRow.set("refresh_token", response.data[0].refresh);
                    this.mainDataSource.activeRow.set("token_expires", response.data[0].expire);
                    this.mainDataSource.activeRow.set("truckstop_tokens_exist", true);

                    this.labelResult.imageName = ImageName.circleCheck;
                    this.labelResult.imageColor = "success";
                    this.textboxTranscorePassword.clear();
                    Snackbar.showSnackbar("Success: Access tokens saved, Truckstop password not saved");
                } else {
                    this.textboxTranscorePassword.clear();
                    this.labelResult.busy = false;
                    this.labelResult.imageName = ImageName.circleX;
                    this.labelResult.imageColor = "error";
                    this.labelResult.tooltip = response.data[0].error ? response.data[0].error : "There was an unexpected error Authorizing access."
                    this.mainDataSource.activeRow.set("truckstop_tokens_exist", false);
                }
            }
        );
    }

    validateTruckstop(clientId: string, clientSecret: string, tUser: string, tPass: string, tUrl: string, labelTruckstopResult: Label, responseCallback,textboxTruckstopPbwUserId?:string) {
        if (StringUtil.isEmptyString(clientId)
            || StringUtil.isEmptyString(clientSecret)
            || StringUtil.isEmptyString(tUser)
            || StringUtil.isEmptyString(tPass)
        ) {
            labelTruckstopResult.imageName = null;
            labelTruckstopResult.imageColor = "primary";
            labelTruckstopResult.busy = false;
            labelTruckstopResult.tooltip = null;
            Snackbar.showWarningSnackbar("Please provide all Truckstop fields (Client ID, Secret, Truckstop User ID, Password");
        } else {
            Api.post("lme/powerbroker/truckstop-auth", {
                "id": this.mainDataSource.activeRow.get("id", ""),
                "client_id": clientId,
                "client_secret": clientSecret,
                "truckstop_user": tUser,
                "truckstop_pass": tPass,
                "truckstop_url": tUrl,
                "truckstop_user_id": textboxTruckstopPbwUserId,
            }).then(responseCallback)
                .catch(reason => {
                    labelTruckstopResult.busy = false
                    labelTruckstopResult.tooltip = reason;
                });
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxExcludedOrderTypeId. */
    textboxExcludedOrderTypeIdBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    /** This is an event handler for the onDataDisplay event of textboxClientId. */
    textboxClientIdOnDataDisplay(event: DataDisplayEvent) {
        if (!StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("client_id")) && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("client_secret"))) {
            if (this.mainDataSource?.activeRow?.get("truckstop_tokens_exist")) {
                this.labelResult.imageName = ImageName.circleCheck;
                this.labelResult.imageColor = "success";
            } else {
                this.labelResult.imageName = ImageName.circleX;
                this.labelResult.imageColor = "error";
                this.labelResult.tooltip = "Tokens do not exist for this service. Please authorize the service."
            }
        }
    }

    async addAssociatedUserToConnexionTable(associatedUserTable: Table, pnnUser: any, existingTableRow: TableRow) {
        if (existingTableRow == null && !StringUtil.equalsIgnoreCase(pnnUser?.transcore_password, "x")) {
            const newRowData: ModelRow = await associatedUserTable._createNewRowData();
            newRowData.set("dat_user_id", pnnUser?.transcore_user_id);
            newRowData.set("dat_password", pnnUser?.transcore_password);
            newRowData.set("user_id", pnnUser?.user_id);
            newRowData.setLookupModelData("user_id", { user_id: pnnUser?.user_id });
            associatedUserTable.addRow(newRowData, { mode: TableRowMode.ADD }, {
                display: true,
                addToData: true,
                save: true
            });
        }
    }

    private async addAssociatedUserToTruckstopTable(associatedUserTable: Table, pnnUser: any, existingTableRow: TableRow) {
        if (existingTableRow == null && !StringUtil.isEmptyString(pnnUser?.truckstop_user_email)) {
            const newRowData: ModelRow = await associatedUserTable._createNewRowData();
            newRowData.set("truckstop_user_id", pnnUser?.truckstop_user_email);
            newRowData.set("truckstop_access_token", pnnUser?.access_token);
            newRowData.set("truckstop_refresh_token", pnnUser?.refresh_token);
            newRowData.set("truckstop_token_expires", pnnUser?.token_expiration);
            newRowData.setLookupModelData("user_id", { user_id: pnnUser?.user_id });
            associatedUserTable.addRow(newRowData, { mode: TableRowMode.ADD }, {
                display: true,
                addToData: true,
                save: true
            });
        }
    }

    async postDataSources(): Promise<boolean> {
        try {
            await this.mainDataSource.post();
            await this.sourceDispatchControl.post();
            if (this.updateRateIndexAuthentication) {
                if (this.sourceRateIndexControl.activeRow === undefined) {
                    await this.sourceRateIndexControl.search({ "vendor": "T" });
                    if (this.sourceRateIndexControl?.activeRow) {
                        this.sourceRateIndexControl.activeRow.set("dat_account_id", this.mainDataSource.activeRow.get("user_id"));
                        this.sourceRateIndexControl.activeRow.set("dat_password", this.mainDataSource.activeRow.get("password"));
                    }
                } else {
                    this.sourceRateIndexControl.activeRow.set("dat_account_id", this.mainDataSource.activeRow.get("user_id"));
                    this.sourceRateIndexControl.activeRow.set("dat_password", this.mainDataSource.activeRow.get("password"));
                }
                await this.sourceRateIndexControl.post();
            }
            return true;
        } catch {
            return false;
        }
    }

    getDefaultData() {
        switch (this._vendorId?.toUpperCase()) {
            case "R":
                return this.activeRow?.get("truckstop_default_values");

            case "C":
                return this.activeRow?.get("dat_conn_default_values");

            case "J":
                return this.activeRow?.get("json_default_values");

            case "D":
                return this.activeRow?.get("dat_default_values");

            case "T":
                return this.activeRow?.get("dat_rest_default_values");

            default:
                return null;
        }
    }

    private setUpAuthLabel(responseData: any) {
        if (responseData?.organization_token || responseData?.is_valid_token) {
            this.labelOrgAuthorizationStatus.imageName = ImageName.circleCheck;
            this.labelOrgAuthorizationStatus.imageColor = "success";
            this.labelOrgAuthorizationStatus.tooltip = "Organization successfully authorized";
            this.buttonOrgAuthorize.visible = false;
            this.labelOrgAuthorizationStatus.visible = true;
            this.labelAssociatedUsers.visible = true;
            this.panelDatRestUserTable.visible = true;
        } else {
            this.labelAssociatedUsers.visible = false;
            this.panelDatRestUserTable.visible = false;
            this.buttonOrgAuthorize.visible = true;
            this.labelOrgAuthorizationStatus.imageName = ImageName.circleX;
            this.labelOrgAuthorizationStatus.imageColor = "error";
            this.labelOrgAuthorizationStatus.tooltip = responseData?.dat_org_auth_error || "Failed to authorize service account";
            Snackbar.showWarningSnackbar(responseData?.dat_org_auth_error || "Failed to authorize service account");
        }
    }


    private promptUserToUpdateCredentials() {
        this.promptTriggered = true;
        try {
            const dialogLayout = Layout.getLayout("lme/general/UpdateCredentialsDialog") as UpdateCredentialsDialog;
            CommonDialogs.showYesNo(dialogLayout, "Edit Account ID & Password", {
                yesButtonCaption: "Yes",
                noButtonCaption: "No",
                xVisible: true,
            }, true, 100).then(clicked => {
                if (clicked) {
                    this.buttonOrgAuthorize.visible = true;
                    this.buttonOrgAuthorize.enabled = true;
                    this.labelOrgAuthorizationStatus.visible = false;
                    this.setupButtonOrganizationAuthTooltip();
                    this.updateRateIndexAuthentication = true;
                } else {
                    // Restore original values if user cancels
                    this.mainDataSource.activeRow.set("password", this.originalPassword);
                    this.mainDataSource.activeRow.set("user_id", this.originalAccountId);
                    this.updateRateIndexAuthentication = false;
                    this.buttonOrgAuthorize.visible = false;
                    this.labelOrgAuthorizationStatus.visible = false;
                }
            });
        } catch (error) {
            this.promptTriggered = false;
            Snackbar.showWarningSnackbar("Error opening credentials dialog");
            console.error("Failed to open update credentials dialog:", error);
        }
    }
}
